import React, { useCallback, useMemo, useRef, useState } from "react";

type PresetType = "pdf" | "excel" | "word";

interface UploadFileDropzoneProps {
  label?: string;
  required?: boolean;
  maxSizeMB?: number;
  value: File | null;
  onChange: (file: File | null) => void;
  error?: string;
  disabled?: boolean;
  className?: string;
  existingUrl?: string | null;
  types?: (PresetType | string)[];
}

const PRESET_MIMES: Record<PresetType, string[]> = {
  pdf: ["application/pdf"],
  excel: [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
  ],
  word: [
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ],
};

const PRESET_EXTS: Record<PresetType, string[]> = {
  pdf: [".pdf"],
  excel: [".xls", ".xlsx"],
  word: [".doc", ".docx"],
};

function normalizeTypes(types?: (PresetType | string)[]) {
  if (!types || types.length === 0)
    return { mimes: [] as string[], exts: [] as string[] };
  const mimes: string[] = [];
  const exts: string[] = [];
  for (const t of types) {
    if ((["pdf", "excel", "word"] as PresetType[]).includes(t as PresetType)) {
      const key = t as PresetType;
      mimes.push(...PRESET_MIMES[key]);
      exts.push(...PRESET_EXTS[key]);
    } else if (typeof t === "string") {
      if (t.startsWith(".")) exts.push(t.toLowerCase());
      else mimes.push(t.toLowerCase());
    }
  }
  return { mimes: Array.from(new Set(mimes)), exts: Array.from(new Set(exts)) };
}

function humanizeTypes(types?: (PresetType | string)[]) {
  if (!types || types.length === 0) return "orice format";
  return types
    .map((t) =>
      String(t)
        .replace("application/", "")
        .replace("vnd.", "")
        .replace(/[.-]/g, " ")
        .toUpperCase(),
    )
    .join(", ");
}

const UploadFileDropzone: React.FC<UploadFileDropzoneProps> = ({
  label = "Fișier",
  required = false,
  maxSizeMB = 20,
  value,
  onChange,
  error,
  disabled = false,
  className = "",
  existingUrl = null,
  types = [],
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const { mimes, exts } = useMemo(() => normalizeTypes(types), [types]);
  const acceptAttr: string | undefined = useMemo(() => {
    if (mimes.length === 0 && exts.length === 0) return undefined;
    return [...mimes, ...exts].join(",");
  }, [mimes, exts]);
  const allowedHuman = useMemo(() => humanizeTypes(types), [types]);

  const prevent = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    prevent(e);
    if (!disabled) setIsDragging(true);
  };
  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    prevent(e);
    if (!disabled) setIsDragging(true);
  };
  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    prevent(e);
    if (!disabled) setIsDragging(false);
  };

  const validateFile = (f: File | null): string | null => {
    if (!f) return null;
    if (f.size > maxSizeMB * 1024 * 1024)
      return `Fișier prea mare (max ${maxSizeMB}MB).`;
    if (mimes.length === 0 && exts.length === 0) return null;

    const typeOk = mimes.length ? mimes.includes(f.type) : true;
    const name = f.name.toLowerCase();
    const extOk = exts.length ? exts.some((ext) => name.endsWith(ext)) : true;

    if (!typeOk && !extOk) return `Tip neacceptat. Permise: ${allowedHuman}.`;
    return null;
  };

  const handleIncomingFile = (f: File | null) => {
    const msg = validateFile(f);
    setLocalError(msg);
    onChange(msg ? null : f);
  };

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      prevent(e);
      if (disabled) return;
      setIsDragging(false);
      const f = e.dataTransfer.files?.[0] ?? null;
      handleIncomingFile(f);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disabled],
  );

  const openPicker = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const borderColor =
    error || localError
      ? "border-danger"
      : isDragging
      ? "border-brand"
      : "border-slate-300";

  const zoneClasses = [
    "rounded-control",
    "p-3",
    "relative",
    "flex",
    "items-center",
    "justify-between",
    "gap-3",
    borderColor,
    disabled
      ? "bg-surface-subtle"
      : isDragging
      ? "bg-brand-subtle"
      : "bg-surface-subtle",
    "select-none",
  ].join(" ");

  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-2">
        <span className="mb-2 block font-medium m-0">{label}</span>
        {required && (
          <span className="inline-flex items-center rounded-control bg-surface-subtle px-3 py-1 text-xs font-semibold text-muted-dark">
            obligatoriu
          </span>
        )}
      </div>

      <div
        className={zoneClasses}
        style={{
          borderStyle: "dashed",
          borderWidth: "2px",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
        onClick={openPicker}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={handleDrop}
        aria-disabled={disabled}
        role="button">
        <div className="flex-grow">
          <div className="font-semibold">
            Trage fișierul aici sau <u>alege din calculator</u>
          </div>
          <div className="text-sm text-muted">
            Formate acceptate: {allowedHuman}. Maxim {maxSizeMB}MB.
          </div>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={acceptAttr}
          className="hidden"
          disabled={disabled}
          onChange={(e) =>
            handleIncomingFile(e.currentTarget.files?.[0] ?? null)
          }
        />

        <div className="text-right" style={{ minWidth: 160, maxWidth: 280 }}>
          {value ? (
            <>
              <div className="font-semibold text-truncate" title={value.name}>
                {value.name}
              </div>
              <div className="text-sm text-muted">
                {(value.size / 1024 / 1024).toFixed(2)} MB
              </div>
            </>
          ) : (
            <div className="text-sm text-muted">Niciun fișier selectat</div>
          )}
        </div>
      </div>

      {existingUrl && (
        <div className="mt-2 text-sm">
          Document existent:{" "}
          <a
            href={existingUrl}
            target="_blank"
            rel="noreferrer"
            className="link-underline">
            deschide
          </a>
        </div>
      )}

      {(error || localError) && (
        <div className="text-danger mt-2 text-sm">{error ?? localError}</div>
      )}
    </div>
  );
};

export default UploadFileDropzone;
